// Components==============
import ControlBar from 'components/Layout/ControlBar/ControlBar';
import ExerciseBottom from 'components/UI/ExerciseBottom/ExerciseBottom';
import ExerciseWrapper from 'components/UI/ExerciseWrapper/ExerciseWrapper';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AudioExerciseContext } from 'screens/AudioExercise/AudioExercise';
import { GlobalContext } from 'utilities/GlobalContext';
import shuffleArray from 'utilities/shuffleArray';
import { useAudio } from 'utilities/useAudio';
import './ClapSoundExercise.module.scss';
import Exercise, { BellStatus } from './components/Exercise/Exercise';
// =========================

type ClapSoundExerciseContextTypes = {
    name: string;
    current: number;
    setCurrent: React.Dispatch<React.SetStateAction<number>>;
    shuffledExercise: ExerciseTypes[];
    answers: BellStatus[];
    setAnswers: React.Dispatch<React.SetStateAction<BellStatus[]>>;
};

export const ClapSoundExerciseContext = createContext(
    {} as ClapSoundExerciseContextTypes
);

export default function ClapSoundExercise() {
    const { exercise, setIsFinished, setProgress, isFinished } = useContext(
        AudioExerciseContext
    );

    const { togglePlay } = useContext(GlobalContext);

    const [current, setCurrent] = useState(0);

    const [shuffledExercise, setShuffledExercise] = useState<ExerciseTypes[]>(
        []
    );

    const [answers, setAnswers] = useState<BellStatus[]>([]);

    const currentWord = shuffledExercise[current - 1];

    useEffect(() => {
        const correctItems = exercise?.exercise?.filter((e) => e?.correct);
        const inCorrectItems = exercise?.exercise?.filter((e) => !e?.correct);

        const amountOfDuplicateCorrect = Math.round(
            inCorrectItems?.length / correctItems?.length
        );

        const duplicateItems = (
            arr: ExerciseTypes[],
            numberOfRepetitions: number
        ) =>
            arr.flatMap(
                (i) =>
                    Array.from({ length: numberOfRepetitions }).fill(
                        i
                    ) as ExerciseTypes[]
            );

        const duplicatedCorrectItems = duplicateItems(
            correctItems,
            amountOfDuplicateCorrect
        );

        const combinedArrays = [...duplicatedCorrectItems, ...inCorrectItems];

        shuffleArray(combinedArrays);

        setShuffledExercise(combinedArrays);
    }, [exercise?.exercise]);

    const exerciseIsOver = current - 1 === shuffledExercise.length;
    const answer = shuffledExercise?.[current - 1];

    useEffect(() => {
        if (current === 0)
            return setProgress({ total: shuffledExercise?.length, done: 0 });

        togglePlay();
        setProgress({ total: shuffledExercise?.length, done: current - 1 });

        if (exerciseIsOver) return setIsFinished(true);
    }, [current, shuffledExercise?.length]);

    const { play, duration } = useAudio({ text: exercise?.name });

    const handleClick =
        current === 0
            ? () => {
                  play();
                  setTimeout(() => {
                      setCurrent(1);
                  }, (duration || 1500) * 1.5);
              }
            : undefined;

    const audio =
        exercise?.type === 'clap_sound' ? (answer?.audio as string) : undefined;
    const text =
        exercise?.type === 'clap_word'
            ? { textToBeRead: answer?.name, phoneme: answer?.phoneme }
            : {};

    return (
        <ClapSoundExerciseContext.Provider
            value={{
                name: exercise.name,
                current,
                setCurrent,
                shuffledExercise,
                answers,
                setAnswers,
            }}
        >
            <ExerciseWrapper disableOverflow>
                {!isFinished && <Exercise currentWord={currentWord} />}
            </ExerciseWrapper>
            <ExerciseBottom
                controlBar={
                    <>
                        {!exerciseIsOver && (
                            <ControlBar
                                audioToBePlayed={audio}
                                {...text}
                                next={null}
                                handlePlayClick={handleClick}
                                useToggle
                            />
                        )}
                    </>
                }
            />
        </ClapSoundExerciseContext.Provider>
    );
}
